/* eslint-disable @typescript-eslint/no-unused-vars */
import { yupResolver } from "@hookform/resolvers/yup";
import { ReactElement, useRef, useState } from "react";
import { Form, Tab, Tabs } from "react-bootstrap";
import { FieldErrors, useForm } from "react-hook-form";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { tPaymentProviders } from "../../../../i18n";
import ItemFilterGroup from "../../../../models/itemFiltering/itemFilterGroup";
import { PaymentProvider } from "../../../../models/paymentProviders/paymentProvider";
import { PageTitle } from "../../../common/PageTitle";
import { DefaultProvider } from "../DefaultProvider";
import {
  AddOrEditItemProps,
  ProviderFormValidationCommon,
  mapToAddPaymentMethodConfiguration,
  mapToPaymentMethodConfiguration,
} from "../ProvidersCommon/Common";
import PaymentProviderFiltersDialog from "../ProvidersCommon/PaymentProviderFiltersDialog";

import { addConfiguration, updateConfiguration } from "../../../../services/paymentProviders/paymentProviders.service";
import ProviderButtonToolbar from "../ProvidersCommon/ProviderButtonToolbar";
import {
  CurrentFormModel,
  CurrentPaymentMethodTypes,
  CurrentPaymentProviderConfiguration,
  CurrentProviderDisplayName,
  mapToAddConfiguration,
  mapToUpdateConfiguration,
} from "./goCardlessConfiguration";
import GoCardlessGeneralSettingsTab from "./GoCardlessGeneralSettingsTab";
import TabRef from "../../../../components/Tabs/TabWithReference";
import { TabArray, selectTabFromValidationErrors } from "../../../../helpers/hookForms/hookFormHelpers";

const EventKeyGeneralSettings = "general";

interface CurrentItemProps extends AddOrEditItemProps<CurrentPaymentProviderConfiguration, CurrentFormModel> {
  data?: CurrentPaymentProviderConfiguration;
  paymentProvider?: PaymentProvider;
  onSave: () => void;
}

/*
 * Form validation schema
 */
const validationSchema = Yup.object({
  ...ProviderFormValidationCommon,
  useStripeCustomer: Yup.boolean(),
  storePayerPaymentMethod: Yup.boolean(),
  saveCardOptionDefault: Yup.boolean(),
}).required();

/*
 * The GoCardless edit payment pro
 */
const GoCardlessItem = ({ data, paymentProvider, onSave }: CurrentItemProps): ReactElement => {
  const { t } = useTranslation(tPaymentProviders.ns);
  const [selectedTab, setSelectedTab] = useState<string | null>(EventKeyGeneralSettings);

  const [filterGroup, setFilterGroup] = useState<ItemFilterGroup | undefined>(undefined);
  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const tabRefs = useRef<TabArray>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(validationSchema) });

  const handleSave = async (model: CurrentFormModel): Promise<void> => {
    setSaving(true);

    // New configuration?
    if (!data) {
      const addConfig = mapToAddConfiguration(model);
      const methods = CurrentPaymentMethodTypes.map((x) => mapToAddPaymentMethodConfiguration(x, model.enabled, filterGroup));
      addConfig.paymentMethodConfigurations = methods;
      if (await addConfiguration(addConfig).finally(() => setSaving(false))) {
        onSave();
      }
    } else {
      const configuration = mapToUpdateConfiguration(data, model);
      const methods = data.paymentMethodConfigurations.map((x) => mapToPaymentMethodConfiguration(x, model.enabled, filterGroup));
      configuration.paymentMethodConfigurations = methods;
      await updateConfiguration(configuration).finally(() => setSaving(false));
      onSave();
    }
  };

  // Handles validation errors on the form, and selects the tab with the first error
  const handleValidationErrors = (errorData: FieldErrors<CurrentFormModel>): void => {
    selectTabFromValidationErrors(tabRefs, errorData, setSelectedTab);
  };

  const providerId = data?.id;

  return (
    <section className='custom-connect-page'>
      <DefaultProvider paymentProviderId={providerId} allowSetDefault>
        <PageTitle title={t("paymentProviders.titleEditConfiguration", { providerName: CurrentProviderDisplayName })} />
      </DefaultProvider>
      <Form
        className='mt-2'
        noValidate
        onSubmit={handleSubmit(
          (submitData) => handleSave(submitData as CurrentFormModel),
          (errorData) => handleValidationErrors(errorData)
        )}
      >
        <Tabs
          defaultActiveKey={EventKeyGeneralSettings}
          className='mb-2'
          activeKey={selectedTab ?? EventKeyGeneralSettings}
          onSelect={(tab) => setSelectedTab(tab)}
        >
          <Tab eventKey={EventKeyGeneralSettings} title={t("commonTableTitles.generalSettingsTabTitle")}>
            <TabRef eventKey={EventKeyGeneralSettings} index={0} tabRefs={tabRefs}>
              <GoCardlessGeneralSettingsTab
                data={data}
                paymentProvider={paymentProvider}
                itemFilterGroup={filterGroup}
                onShowFilterDialog={() => setShowFilterDialog(true)}
                register={register}
                errors={errors}
              />
            </TabRef>
          </Tab>
        </Tabs>
        <ProviderButtonToolbar saving={saving} />
      </Form>
      <PaymentProviderFiltersDialog
        filterGroup={filterGroup}
        show={showFilterDialog}
        onUpdate={(group) => {
          setShowFilterDialog(false);
          setFilterGroup(group);
        }}
        onClose={() => setShowFilterDialog(false)}
      />
    </section>
  );
};

GoCardlessItem.defaultProps = {
  data: undefined,
  paymentProvider: undefined,
};

export default GoCardlessItem;
